import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { CURRENCIES, currencyLabel } from '@/utils/currency';

/**
 * Searchable currency dropdown with a "suggested" shortlist on top.
 * Props:
 *   value: ISO code currently selected
 *   onChange(code): called when user picks a currency
 *   suggestedCodes: list of ISO codes to surface at the top (e.g. ['MVR','USD','EUR','GBP'])
 *   testid: data-testid prefix
 */
export default function CurrencyPicker({ value, onChange, suggestedCodes = [], testid = 'currency-picker' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const suggestedSet = useMemo(() => new Set(suggestedCodes.filter(Boolean)), [suggestedCodes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CURRENCIES;
    return CURRENCIES.filter(c =>
      c.code.toLowerCase().includes(q) ||
      c.name.toLowerCase().includes(q) ||
      (c.symbol && c.symbol.toLowerCase().includes(q))
    );
  }, [query]);

  // Order: suggested first (in given order), then alphabetical rest, all filtered.
  const ordered = useMemo(() => {
    if (query) return filtered;
    const top = suggestedCodes
      .map(code => CURRENCIES.find(c => c.code === code))
      .filter(Boolean);
    const rest = CURRENCIES
      .filter(c => !suggestedSet.has(c.code))
      .sort((a, b) => a.code.localeCompare(b.code));
    return [...top, ...rest];
  }, [filtered, query, suggestedCodes, suggestedSet]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(o => !o)}
        className="w-full inline-flex items-center justify-between gap-2 px-3 h-10 rounded-md border border-stone-200 bg-white text-left text-sm hover:border-stone-300 transition-colors"
      >
        <span className="truncate text-stone-800">{currencyLabel(value || 'USD')}</span>
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-80 rounded-lg border border-stone-200 bg-white shadow-lg overflow-hidden flex flex-col">
          <div className="p-2 border-b border-stone-100">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-stone-50 border border-stone-200">
              <Search size={14} className="text-stone-400" />
              <input
                autoFocus
                data-testid={`${testid}-search`}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search currency, code or symbol…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400"
              />
            </div>
          </div>
          <div className="overflow-y-auto">
            {!query && suggestedCodes.length > 0 && (
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Suggested</div>
            )}
            {ordered.length === 0 && (
              <p className="px-3 py-4 text-sm text-stone-400 text-center">No matches</p>
            )}
            {ordered.map((c, idx) => {
              const isSelected = c.code === value;
              const isLastSuggested = !query && idx === suggestedCodes.length - 1 && suggestedSet.has(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`${testid}-option-${c.code}`}
                  onClick={() => { onChange(c.code); setOpen(false); setQuery(''); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-stone-50 transition-colors ${
                    isSelected ? 'bg-orange-50 text-orange-700' : 'text-stone-700'
                  } ${isLastSuggested ? 'border-b border-stone-100 mb-1' : ''}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-10 text-stone-500 font-mono text-xs">{c.symbol}</span>
                    <span className="font-semibold w-12">{c.code}</span>
                    <span className="truncate text-stone-500">{c.name}</span>
                  </span>
                  {isSelected && <Check size={14} className="text-orange-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
